import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import MUITable from "Components/UI/Tables/MUITable";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Modal,
  styled,
  TableCell,
  TableRow,
  Typography,
  Collapse,
  Grid,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  getInvoiceTypeChip,
  getSubStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import {
  getParseStatusColorByLabel,
  getSystemStatusColorById,
  getSubStatusColorById,
} from "utils/colors";
import useTableOptions from "Hooks/useTableOptions";
import CommonChip from "Components/UI/Tables/CommonChip";
import AddBtn from "Components/UI/Tables/AddBtn";
import MessageIcon from "@mui/icons-material/Message";
import Cancel from "@mui/icons-material/Cancel";
import { useAuth } from "Hooks/useAuth";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { LoadingButton } from "@mui/lab";
import { applyCreditToInvoice } from "API/swesum";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import EndcustomerAccountModal from "Components/Endcustomer/EndcustomerAccountModal";
import EndcustomerAccountsTableContainer from "Components/Account/EndcustomerAccountTableContainer";
import { debounceSearchRender } from "mui-datatables";
import { formatPrice } from "utils/priceformatter";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import HoverableMessageIcon from "Components/UI/Popovers/HoverableMessageIcon";

const StyledTable = styled(MUITable)(({ theme }) => ({
  border: "none",
  boxShadow: "none",
  ".MuiTable-root": {
    ".MuiTableCell-root": {
      "&:nth-of-type(1)": {
        paddingTop: "0",
        paddingBottom: "0",
      },
    },
  },
  "& .MuiTableCell-root": {
    //backgroundColor: "#f5f5f5",
    //padding: "14px",
  },
}));

const createRows = (invoices) => {
  return invoices?.map((i) => {
    return [
      i.Id,
      i.InvoiceDate,
      i.CalcFfdDate ?? i.OriginalFfdDate,
      {
        id: i.Id,
        type: i.Type,
        remainingAmount: i.RemainingAmount,
        label: i.Type === "Credit" ? "Kredit" : "Faktura",
      },
      { label: i.ExternalInvoiceNo, invoiceId: i.Id },
      { label: i.Substatus, systemStatus: i.SystemStatus, type: i.Type },
      i.ReminderStopped,
      i.BundleId,
      i.ReminderRemainingAmount + i.RemainingInterestAmount,
      i.RemainingAmount,
      i.Price === null ? 0 : i.Type === "Credit" ? i.Price * -1 : i.Price,
      { HasCustomNotes: i.HasCustomNotes, Logs: i.Logs },
      { label: i.EndCustomerExternalCustomerNo, id: i.EndCustomerId },
      {
        label: getSystemStatusLabelById(i.SystemStatus),
        color: getSystemStatusColorById(i.SystemStatus),
      },
      i.Client?.Name,
      { label: i.EndCustomerName, id: i.EndCustomerId },
      i.Service === "purchase"
        ? "Köp"
        : i.Service === "service"
        ? "Service"
        : "",
      i.OCR,
      i.Regress,
      i.RegressDate,
      i?.ClosedDate?.split(" ")[0],
      i.AdjustedAmount,
      i.Migrated === 1 ? "Ja" : "Nej",
    ];
  });
};

const getSubStatusChip = (substatus) => {
  let color = getSubStatusColorById(substatus);
  let label = getSubStatusLabelById(substatus);

  return <Chip label={label} color={color} />;
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const columnsToHideClient = ["Id", "ClientName", "Migrerad", "Stängd"];

export default function InvoiceTable({
  invoices,
  endcustomerArticleMap,
  openCreateInvoice,
  onChangePage,
  onChangeRowsPerPage,
  onChangeSort,
  currentPage,
  rowsPerPage,
  totalRows,
  onUpdate,
  sortOrder,
  onSearch,
  invoicesLoading,
  getAllOnDownload,
  isEndcustomer = false,
}) {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [selectedCreditId, setSelectedCreditId] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const { openSuccess, openError } = usePopupNotificationContext();
  const [selectedEndCustomerEvent, setSelectedEndCustomerEvent] =
    useState(null);

  const [openConfirmConnectionModal, setOpenConfirmConnectionModal] =
    useState(false);

  const { handleColumnShow, isColDisplayed, isNew } =
    useTableOptions("InvoicesMainTable");

  useEffect(() => {
    const rows = createRows(invoices);
    setRows(rows);
  }, [invoices]);

  useEffect(() => {
    if (selectedCreditId && selectedInvoiceId) {
      setOpenConfirmConnectionModal(true);
    } else {
      setOpenConfirmConnectionModal(false);
    }
  }, [selectedCreditId, selectedInvoiceId]);

  useEffect(() => {
    if (isNew && !isAdmin) {
      for (const col of columnsToHideClient) {
        handleColumnShow(col, "remove");
      }
    }
  }, [isAdmin, isNew]);

  const handleOnConnectPrimary = (event, id) => {
    event.stopPropagation();

    setSelectedCreditId(selectedCreditId === id ? null : id);
  };

  const handleOnConnectSecondary = (id) => {
    if (selectedCreditId && Number(selectedCreditId) !== Number(id)) {
      setOpenConfirmConnectionModal(true);
      setSelectedInvoiceId(id);
    }
  };

  const checkColumnDisplay = (colIndex) => {
    const col = columns[colIndex];
    if (col) {
      return isColDisplayed(col.name);
    }
  };

  const handleOnConnect = async (creditId, invoiceId) => {
    setModalLoading(true);
    try {
      const res = await applyCreditToInvoice(creditId, invoiceId);
      if (res) {
        if (onUpdate) {
          onUpdate();
        }
        openSuccess("OK!");
      }
    } catch (err) {
      openError(err.response.data, 2000);
    }
    setOpenConfirmConnectionModal(false);
    setSelectedCreditId(null);
    setSelectedInvoiceId(null);
    setModalLoading(false);
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const onDownload = async (columns) => {
    try {
      const res = await getAllOnDownload();
      if (!res) {
        openError("Någonting gick fel vid nedladdning", 3000);
        return false;
      }

      const rows = createRows(res.Rows.sort((r1, r2) => r1.Id - r2.Id));
      const newRows = rows.map((row, index) => ({ index: index, data: row }));
      const csvData = newRows.map(({ data, index }) => {
        const rowData = data.map((r1) => {
          if (r1 && typeof r1 === "object" && r1.label !== null) {
            return r1.label;
          }
          return r1;
        });
        return { index: index, data: rowData };
      });

      const csvContent =
        "\uFEFF" +
        columns.map((col) => col.label || col.name).join(";") +
        "\n" +
        (csvData || [])
          .map((row) => {
            const cells = (row?.data || []).map((cell) =>
              cell === null || cell === undefined ? "" : `"${cell}"`
            );
            return cells.join(";");
          })
          .join("\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", "client_invoices.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return false;
    } catch (error) {
      console.error("Error in onDownload:", error);
      openError("An unexpected error occurred during download", 3000);
    } finally {
      return false;
    }
  };

  const columns = [
    {
      label: "",
      name: "",
      empty: false,
      options: {
        display: isEndcustomer,
        customBodyRender: (id) => {
          return (
            <IconButton
              key={id}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleRowExpansion(id);
              }}
            >
              {expandedRow === id ? (
                <KeyboardArrowDown />
              ) : (
                <KeyboardArrowRight />
              )}
            </IconButton>
          );
        },
      },
    },

    {
      label: "Fakturadatum",
      name: "InvoiceDate",
      options: {
        display: isColDisplayed("InvoiceDate"),
        filter: false,
      },
    },
    {
      label: "Förfallodatum",
      name: "OriginalFfdDate",
      options: {
        display: isColDisplayed("OriginalFfdDate"),
        filter: false,
      },
    },
    {
      label: "Typ",
      name: "Type",
      options: {
        filter: false,
        display: isColDisplayed("Type"),
        customBodyRender: (value) => {
          const chip = getInvoiceTypeChip(value.type);
          if (value.type && value.type === "Credit") {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <Chip
                  label={chip.label}
                  sx={{ background: chip.color, color: "white" }}
                />
                {isEndcustomer && value.remainingAmount !== 0 && (
                  <IconButton
                    onClick={(event) => handleOnConnectPrimary(event, value.id)}
                  >
                    {value.id === selectedCreditId ? (
                      <RemoveIcon color="primary" />
                    ) : (
                      <AddIcon color="primary" />
                    )}
                  </IconButton>
                )}
              </div>
            );
          } else {
            return (
              <Chip
                label={chip.label}
                sx={{ background: chip.color, color: "white" }}
              />
            );
          }
        },
      },
    },
    {
      label: "Fakturanummer",
      name: "ExternalInvoiceNo",
      options: {
        filter: false,
        display: isColDisplayed("ExternalInvoiceNo"),
        customBodyRender: (value) => {
          if (isAdmin) {
            return (
              <Link to={`/invoices/${value.invoiceId}`}>{value.label}</Link>
            );
          } else {
            return (
              <Link to={`/clientInvoices/${value.invoiceId}`}>
                {value.label}
              </Link>
            );
          }
        },
      },
    },
    {
      name: "Substatus",
      options: {
        filter: false,
        sort: false,
        display: isColDisplayed("Substatus"),
        customBodyRender: (value) => {
          if (value.systemStatus === "2" || value.type === "Credit")
            return null;
          return getSubStatusChip(value.label);
        },
      },
    },
    {
      label: "Stoppad",
      name: "ReminderStopped",
      options: {
        sort: false,
        filter: false,
        display: isColDisplayed("ReminderStopped"),
        customBodyRender: (value) => {
          return <>{value ? <Cancel color="warning" /> : null}</>;
        },
      },
    },
    {
      label: "Buntnummer",
      name: "BundleId",
      options: {
        display: isColDisplayed("BundleId"),
        filter: false,
      },
    },
    {
      name: "Avgifter",
      options: {
        display: isColDisplayed("Avgifter"),
        sort: false,
        filter: false,
        customBodyRender: (value) => (
          <div style={{ textAlign: "right" }}>{formatPrice(value)}</div>
        ),
      },
    },
    {
      name: "Återstående belopp",
      options: {
        display: isColDisplayed("Återstående belopp"),
        sort: false,
        filter: false,
        customBodyRender: (value) => (
          <div style={{ textAlign: "right" }}>{formatPrice(value)}</div>
        ),
      },
    },
    {
      name: "Ursprungligt belopp",
      options: {
        display: isColDisplayed("Ursprungligt belopp"),
        sort: false,
        filter: false,
        customBodyRender: (value) => (
          <div style={{ textAlign: "right" }}>{formatPrice(value)}</div>
        ),
      },
    },
    {
      name: "Kommentar",
      options: {
        sort: false,
        filter: false,
        display: isColDisplayed("Kommentar"),
        customBodyRender: (value) => {
          return value.HasCustomNotes ? (
            <HoverableMessageIcon logs={value.Logs} />
          ) : null;
        },
      },
    },
    {
      label: "Kundnummer",
      name: "ClientNumber",
      options: {
        display: isColDisplayed("ClientNumber"),
        customBodyRender: (value) => {
          return value.label;
        },
      },
    },
    {
      label: "Status",
      name: "SystemStatus",
      options: {
        filter: false,
        display: isColDisplayed("SystemStatus"),
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value.label}
              color={getSystemStatusColorById(value.color)}
            />
          );
        },
      },
    },
    {
      label: "Klientnamn",
      name: "ClientName",
      options: {
        filter: true,
        display: isColDisplayed("ClientName"),
        empty: false,
        customBodyRender: (value) => {
          if (isAdmin) {
            return value;
          } else {
            return null;
          }
        },
      },
    },
    {
      label: "Kundnamn",
      name: "EndCustomerName",
      options: {
        display: isColDisplayed("EndCustomerName"),
        customBodyRender: (value) => {
          if (isAdmin) {
            return <Link to={`/endcustomers/${value.id}`}>{value.label}</Link>;
          } else {
            return <Link to={`endcustomers/${value.id}`}>{value.label}</Link>;
          }
        },
        filter: false,
      },
    },
    {
      label: "Fakturatjänst",
      name: "Service",
      options: {
        display: isColDisplayed("Service"),
        filter: false,
      },
    },
    {
      label: "OCR",
      name: "OCR",
      options: {
        display: isColDisplayed("OCR"),
        filter: false,
      },
    },
    {
      label: "Regress",
      name: "Regress",
      options: {
        display: isColDisplayed("Regress"),
        filter: false,
      },
    },
    {
      name: "Regressdatum",
      options: {
        sort: false,
        display: isColDisplayed("Regressdatum"),
        filter: false,
      },
    },
    {
      name: "Stängd",
      options: {
        sort: false,
        display: isColDisplayed("Stängd"),
        filter: false,
      },
    },
    {
      name: "Avräknat belopp",
      options: {
        sort: false,
        display: isColDisplayed("Avräknat belopp"),
        filter: false,
      },
    },
    {
      name: "Migrerad",
      options: {
        sort: false,
        display: isColDisplayed("Migrerad"),
        filter: false,
      },
    },
  ];

  const options = {
    customToolbar: () => {
      return (
        <>
          <Tooltip title={"Download CSV"}>
            <IconButton onClick={() => onDownload(columns)}>
              <CloudDownloadIcon />
            </IconButton>
          </Tooltip>
          {openCreateInvoice ? (
            <AddBtn onClick={openCreateInvoice} title={"Skapa faktura"} />
          ) : null}
        </>
      );
    },
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
    filterType: "dropdown",
    search: !!onSearch,
    onSearchChange: (searchText) => (onSearch ? onSearch(searchText) : null),
    onChangePage: (currentPage) =>
      onChangePage ? onChangePage(currentPage) : null,
    onViewColumnsChange: (changedColumn, action) =>
      handleColumnShow(changedColumn, action),
    onChangeRowsPerPage: (numberOfRows) =>
      onChangeRowsPerPage ? onChangeRowsPerPage(numberOfRows) : null,
    onColumnSortChange: (changedColumn, direction) => {
      if (onChangeSort)
        onChangeSort(changedColumn, direction === "asc" ? 0 : 1);
    },
    customSearch: () => true,
    customSearchRender: debounceSearchRender(500),
    sortOrder: {
      name: sortOrder?.Column,
      direction: sortOrder?.Order === 0 ? "asc" : "desc",
    },
    rowsPerPage: rowsPerPage,
    page: currentPage,
    count: totalRows,
    rowsPerPageOptions: [10, 30, 100],
    textLabels: {
      body: {
        noMatch: invoicesLoading ? <CircularProgress /> : "",
      },
    },
    download: false,
    customRowRender: (data, _, rowIndex) => {
      const invoice = invoices?.find(
        (i) => Number(i.Id) === Number(data[0].key)
      );
      if (!invoice) {
        return null;
      }

      return (
        <React.Fragment key={rowIndex}>
          <TableRow
            style={{
              background:
                selectedCreditId === invoice.Id
                  ? "rgba(25, 118, 210, 0.08)"
                  : null,
              opacity: selectedCreditId && invoice?.type === "Credit" ? 0.5 : 1,
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (invoice?.type !== "Credit") {
                handleOnConnectSecondary(invoice.Id);
              }
            }}
          >
            {data.map((cell, cellIndex) => {
              if (!checkColumnDisplay(cellIndex)) return null;

              if (cellIndex === 0 && !isEndcustomer) return null;

              return <TableCell key={cellIndex}>{cell}</TableCell>;
            })}
          </TableRow>

          <TableRow>
            <TableCell colSpan={data.length - 3}>
              <Collapse in={expandedRow === invoice?.Id} unmountOnExit>
                <Grid item container p={1}>
                  <EndcustomerAccountsTableContainer
                    accounts={invoice?.EndCustomerAccounts}
                    endcustomerArticleMap={endcustomerArticleMap}
                    title={
                      <Typography style={{ fontSize: 20 }}>
                        Händelser
                      </Typography>
                    }
                  />
                  {/*} <EventTable
                    events={invoice?.EndCustomerAccounts}
                    endcustomerArticleMap={endcustomerArticleMap}
                  />*/}
                </Grid>
              </Collapse>
            </TableCell>
          </TableRow>
        </React.Fragment>
      );
    },
  };

  return (
    <>
      {invoicesLoading && rows.length > 0 ? (
        <CircularProgress size="20px" />
      ) : null}
      <StyledTable options={options} data={rows} columns={columns} />
      <EndcustomerAccountModal
        open={selectedEndCustomerEvent}
        onClose={() => setSelectedEndCustomerEvent(null)}
        endcustomerAccount={selectedEndCustomerEvent}
      />
      <Modal
        onClose={() => {
          setOpenConfirmConnectionModal(false);
          setSelectedCreditId(null);
          setSelectedInvoiceId(null);
        }}
        open={openConfirmConnectionModal}
      >
        <Box sx={style}>
          <Typography variant="h6">{"Koppla kredit mot faktura"}</Typography>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <p>
              {`Vill du koppla krediten ${
                invoices?.find((i) => i.Id === Number(selectedCreditId))
                  ?.ExternalInvoiceNo ?? "Unknown Credit"
              } till faktura med fakturanummer ${
                invoices?.find((i) => i.Id === Number(selectedInvoiceId))
                  ?.ExternalInvoiceNo ?? "Unknown Invoice"
              }?`}
            </p>
            <div style={{ display: "flex", gap: "10px" }}>
              <LoadingButton
                variant="contained"
                loading={modalLoading}
                onClick={() =>
                  handleOnConnect(selectedCreditId, selectedInvoiceId)
                }
              >
                Koppla
              </LoadingButton>
              <Button
                onClick={() => {
                  setOpenConfirmConnectionModal(false);
                  setSelectedCreditId(null);
                  setSelectedInvoiceId(null);
                }}
              >
                Avbryt
              </Button>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}
